import React, { useState, useEffect } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { StylesProvider } from '@material-ui/core/styles';
import MaterialTable from 'material-table';

import { createTableIcons } from '../../../shared/styles/createTableIcons';
import { generateClassName } from '../../../shared/styles/generateClassName';
import SnackConfig from '../../../shared/config/snackConfig';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import { queryChannels, updateChannel, UpdateChannelRequest } from '../../../shared/services/channel';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    padding: '20px',
    boxSizing: 'border-box',
  },
  maxBorderBox: {
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
}));

interface TableChannelRowData {
  id: number;
  name: string;
  type: string;
  emails: string;
}

interface Form {
  name: string;
  emails: string;
}

const NotificationChannels: React.FC = () => {
  const classes = useStyles();

  const [channels, setChannels] = useState<TableChannelRowData[]>([]);
  const [loadingChannels, setLoadingChannels] = useState<boolean>(false);
  const [snackConfig, setSnackConfig] = useState<SnackConfig>(new SnackConfig({}));

  useEffect(() => {
    void (async () => {
      try {
        setLoadingChannels(true);
        const data = await queryChannels();
        if (data && data.channels) {
          setChannels(
            data.channels.map(({ id, name, type, emails }) => {
              const tableChannel: TableChannelRowData = {
                id,
                name,
                type,
                emails: emails.join(', '),
              };
              return tableChannel;
            })
          );
        }
      } catch (error) {
        setSnackConfig(
          new SnackConfig({
            open: true,
            message: JSON.stringify((error as Error).message),
          })
        );
      }
      setLoadingChannels(false);
    })();
  }, []);

  const update = async (newData: TableChannelRowData): Promise<boolean> => {
    let successfullyUpdated = false;
    let newEmails: string[] = [];
    if (newData.emails.trim().length !== 0) {
      newEmails = newData.emails.split(',').map((email) => email.trim());
    }
    const request: UpdateChannelRequest = {
      id: newData.id,
      name: newData.name,
      type: newData.type,
      emails: newEmails,
    };
    const result = await updateChannel(request);
    if (result && result.updateChannel) {
      successfullyUpdated = true;
      const index = channels.findIndex((channel) => channel.id === newData.id);
      const { id, name, type, emails } = result.updateChannel;
      const updatedChannel: TableChannelRowData = {
        id,
        name,
        type,
        emails: emails.join(', '),
      };
      const updatedChannels = channels.slice();
      updatedChannels[index] = updatedChannel;
      setChannels(updatedChannels);
    }
    return successfullyUpdated;
  };

  return (
    <div className={classes.container}>
      <div className={classes.maxBorderBox}>
        <SpinnerOverlay active={loadingChannels}>
          <StylesProvider generateClassName={generateClassName()}>
            <MaterialTable
              title=""
              options={{
                emptyRowsWhenPaging: false,
              }}
              columns={[
                { title: 'Name', field: 'name', type: 'string', searchable: true, sorting: true },
                { title: 'Type', field: 'type', type: 'string', searchable: true, sorting: true, editable: 'never' },
                {
                  title: (
                    <span>
                      Email(s)
                      <div>
                        <Typography color="textSecondary" variant="caption">
                          Comma separated. Leave empty for no notification
                        </Typography>
                      </div>
                    </span>
                  ),
                  editPlaceholder: 'Email(s)',
                  field: 'emails',
                  type: 'string',
                  searchable: true,
                  sorting: true,
                },
              ]}
              data={channels}
              icons={createTableIcons()}
              editable={{
                onRowUpdate: update,
              }}
            />
          </StylesProvider>
        </SpinnerOverlay>
      </div>
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default NotificationChannels;
